import React from 'react';
import { useNavigate } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import { useBranchNav } from '../../hooks/branchNav.hook';
import {
  CUSTOM_ORDER_STATUS_MAX,
  sortCustomOrderStatuses,
} from '../../types/OrderStatusTypes';
import '../dashboard/Dashboard.css';
import '../shipment/ShipmentsPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import IconButton from '../dashboard/components/product_item_components/IconButton';
import { TrashIcon } from '../dashboard/components/product_item_components/icons';
import DeleteProductModal from '../dashboard/components/product_item_components/DeleteProductModal';

function stagesCountLabel(count) {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return 'этапов';
  if (n1 === 1) return 'этап';
  if (n1 >= 2 && n1 <= 4) return 'этапа';
  return 'этапов';
}

function CustomOrderStatusesPage() {
  const navigate = useNavigate();
  const { branchHomePath } = useBranchNav();
  const { loading: httpLoading, requestWithMeta } = useHttp();

  const [statuses, setStatuses] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [statusToDelete, setStatusToDelete] = React.useState(null);
  const [deleteError, setDeleteError] = React.useState(null);

  const fetchStatuses = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await requestWithMeta('/operator/custom-order-statuses', 'GET');
      if (!res?.ok) {
        setError(res?.data?.error || res?.data?.message || 'Не удалось загрузить этапы');
        setStatuses([]);
        return;
      }
      const list = Array.isArray(res?.data?.customOrderStatuses)
        ? res.data.customOrderStatuses
        : [];
      setStatuses(sortCustomOrderStatuses(list));
    } catch (_) {
      setError('Ошибка загрузки');
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  }, [requestWithMeta]);

  React.useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  React.useEffect(() => {
    if (!statusToDelete) setDeleteError(null);
  }, [statusToDelete]);

  const handleBack = () => {
    navigate(branchHomePath);
  };

  const atLimit = statuses.length >= CUSTOM_ORDER_STATUS_MAX;

  const handleCreate = () => {
    if (atLimit) return;
    navigate('/order-statuses/create');
  };

  const handleEdit = (status) => {
    const id = status?.id ?? status?._id;
    if (id == null) return;
    navigate(`/order-statuses/${encodeURIComponent(id)}/edit`, {
      state: { customOrderStatus: status },
    });
  };

  const handleConfirmDelete = async () => {
    const status = statusToDelete;
    const id = status?.id ?? status?._id;
    if (id == null) return;

    setDeleteError(null);
    const res = await requestWithMeta(
      `/operator/custom-order-statuses/${encodeURIComponent(id)}`,
      'DELETE'
    );
    if (!res?.ok) {
      setDeleteError(res?.data?.error || res?.data?.message || 'Не удалось удалить этап');
      return;
    }

    setStatuses((prev) =>
      sortCustomOrderStatuses(
        prev.filter((item) => String(item?.id ?? item?._id) !== String(id))
      )
    );
    setStatusToDelete(null);
  };

  const deleteName =
    statusToDelete?.name ??
    (statusToDelete
      ? `Этап #${statusToDelete?.id ?? statusToDelete?._id}`
      : '');

  return (
    <div className="shipments-page-root">
      <CreateBusinessHeader
        title="Этапы"
        subtitle=""
        onBack={handleBack}
      />

      <main className="shipments-page-content">
        {loading ? (
          <div className="shipments-page-loading" aria-busy="true">
            <div className="shipments-page-skeleton" />
            <div className="shipments-page-skeleton" />
            <div className="shipments-page-skeleton" />
          </div>
        ) : error ? (
          <div className="shipments-page-error">{error}</div>
        ) : (
          <>
            <div className="main-page-sectionHeader shipments-page-sectionHeader">
              <div className="main-page-sectionSub">
                <span>
                  {statuses.length
                    ? `${statuses.length} ${stagesCountLabel(statuses.length)}`
                    : 'Нет этапов'}
                </span>
              </div>
            </div>

            {statuses.length === 0 ? (
              <div className="shipments-page-empty">Нет этапов</div>
            ) : (
              <ul className="shipments-page-list" aria-label="Список этапов">
                {statuses.map((status) => {
                  const id = status?.id ?? status?._id;
                  const name = status?.name ?? `Этап #${id}`;

                  return (
                    <li key={id} className="shipments-page-item">
                      <div className="shipments-page-itemRow">
                        <button
                          type="button"
                          className="shipments-page-itemMain"
                          onClick={() => handleEdit(status)}
                          aria-label={`Редактировать «${name}»`}
                        >
                          <span className="shipments-page-itemName">{name}</span>
                        </button>
                        <div className="shipments-page-itemActions main-page-productActions">
                          <IconButton
                            onClick={() => setStatusToDelete(status)}
                            label={`Удалить «${name}»`}
                            title="Удалить"
                            className="main-page-iconButton"
                          >
                            <TrashIcon />
                          </IconButton>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </main>

      <footer className="shipments-page-footerRow">
        <button
          type="button"
          className="shipments-page-footerButton"
          onClick={handleCreate}
          disabled={atLimit || loading}
        >
          {atLimit ? 'Максимум 10 этапов' : '+ Добавить этап'}
        </button>
      </footer>

      <DeleteProductModal
        isOpen={!!statusToDelete}
        onClose={() => setStatusToDelete(null)}
        title="Удалить этап"
        productName={deleteName}
        deleteError={deleteError}
        loading={httpLoading}
        onConfirmDelete={handleConfirmDelete}
      />
    </div>
  );
}

export default CustomOrderStatusesPage;
